import { ErrorCodes } from "@ai-teach/types";
import toast from "react-hot-toast";
import { hasGraphQLErrorCode } from "./graphqlErrors";

/** Retrying can't help until someone tops up, so the toast names who has to act. */
export const AI_CREDITS_MESSAGE =
  "Out of AI credits. Please ask the developer team to top up the OpenAI account, then try again.";

// Only wording no author's own content could carry: the provider's code and its quota sentence. A
// per-minute 429 is deliberately excluded — that stays a retryable failure. The server's already
// classified message isn't matched either, which is what lets `describeAiError` pass it through
// unchanged and keep the feature it names ("PDF import is out of AI credits…").
const CREDITS_SPENT = /insufficient_quota|exceeded your current quota/i;

/** For credit failures that reach the UI as a bare string — a job's stored error, a payload's `error` field. */
export function isAiCreditsMessage(message: string | null | undefined): boolean {
  return !!message && CREDITS_SPENT.test(message);
}

function errorMessages(error: unknown): string[] {
  if (!error || typeof error !== "object") return [];
  const messages: string[] = [];
  const { errors, message } = error as { errors?: Array<{ message?: string }>; message?: string };
  if (typeof message === "string") messages.push(message);
  for (const nested of errors ?? []) {
    if (typeof nested?.message === "string") messages.push(nested.message);
  }
  return messages;
}

/** The message of the error that actually carries the code — not merely the first one in the response. */
function codedMessage(error: unknown): string | undefined {
  if (!error || typeof error !== "object") return undefined;

  const { errors, extensions, message } = error as {
    errors?: Array<{ message?: string; extensions?: { code?: string } }>;
    extensions?: { code?: string };
    message?: string;
  };

  const coded = errors?.find(
    (nested) => nested?.extensions?.code === ErrorCodes.AI_CREDITS_EXHAUSTED
  );
  if (coded?.message) return coded.message;

  const topLevel = extensions?.code === ErrorCodes.AI_CREDITS_EXHAUSTED ? message : undefined;
  return typeof topLevel === "string" ? topLevel : undefined;
}

/**
 * True when an AI call failed because the provider account is out of credit. Most of the API's AI
 * paths raise the raw provider error rather than `AI_CREDITS_EXHAUSTED`, so the message is matched
 * too — otherwise those failures reach the author as an unactionable generic toast.
 */
export function isAiCreditsExhausted(error: unknown): boolean {
  if (hasGraphQLErrorCode(error, ErrorCodes.AI_CREDITS_EXHAUSTED)) return true;
  return errorMessages(error).some(isAiCreditsMessage);
}

/**
 * For an error already stored as text — a generation job's `error` — swaps the raw provider message
 * for the top-up ask, so the author reads a next step instead of a 429.
 */
export function describeAiError(message: string | null | undefined, fallback = ""): string {
  if (isAiCreditsMessage(message)) return AI_CREDITS_MESSAGE;
  return message ?? fallback;
}

/**
 * Shows the top-up toast if `error` is a credit failure, and reports whether it handled it, so call
 * sites can `if (toastAiCredits(err, id)) return;` ahead of their own generic failure toast.
 *
 * A raw provider message ("429 You exceeded your current quota…") is never shown — only a coded
 * server error, which names the feature, is trusted as author-facing text.
 */
export function toastAiCredits(error: unknown, toastId: string): boolean {
  if (!isAiCreditsExhausted(error)) return false;

  toast.error(codedMessage(error) ?? AI_CREDITS_MESSAGE, { id: toastId, duration: 10000 });
  return true;
}
